import React from 'react';
import { fetchAndActivate, getValue } from 'firebase/remote-config';

import { remoteConfig } from '../services';
import remoteConfigDefaults from '../config/remoteConfigDefaults.json';

type ContextState = {
  economy_jpu_premium_puzzle_prices: string;
  gameplay: {
    default_difficulty: number;
    difficulties_actual_and_display: Record<string, string>;
  };
  /** Main url for gotiva creators' dashboard. */
  host: string;
  storyDifficulities: {
    square: number[];
  };
};

const RemoteConfigContext = React.createContext<ContextState | undefined>(undefined);

/**
 * Context provider for Firebase remote config.
 *
 * @todo: Handle refetch on throttle - not necessary with current small userbase (as of August).
 * @see: https://firebase.google.com/docs/remote-config
 */
const RemoteConfigProvider: React.FC = ({ children }) => {
  const [value, setValue] = React.useState<ContextState | undefined>(undefined);

  // Setup default values while waiting for remote values to get loaded.
  React.useEffect(() => {
    // Setup default values
    if (remoteConfig.lastFetchStatus === 'no-fetch-yet') {
      remoteConfig.defaultConfig = remoteConfigDefaults;

      const gameplay = getValue(remoteConfig, 'gameplay').asString();
      const economy_jpu_premium_puzzle_prices = getValue(
        remoteConfig,
        'economy_jpu_premium_puzzle_prices',
      ).asString();
      const host = getValue(remoteConfig, 'HOST').asString();
      const storyDifficulities = getValue(remoteConfig, 'story_difficulties').asString();

      setValue({
        economy_jpu_premium_puzzle_prices,
        gameplay: gameplay ? JSON.parse(gameplay) : {},
        host,
        storyDifficulities: storyDifficulities ? JSON.parse(storyDifficulities) : {},
      });
    }

    // Fetch remote config and activate it, allowing us to get its values.
    fetchAndActivate(remoteConfig).then(() => {
      if (remoteConfig.lastFetchStatus === 'success') {
        const economy_jpu_premium_puzzle_prices = getValue(
          remoteConfig,
          'economy_jpu_premium_puzzle_prices',
        ).asString();
        const gameplay = getValue(remoteConfig, 'gameplay').asString();
        const host = getValue(remoteConfig, 'HOST').asString();
        const storyDifficulities = getValue(remoteConfig, 'story_difficulties').asString();

        setValue({
          economy_jpu_premium_puzzle_prices,
          gameplay: gameplay ? JSON.parse(gameplay) : {},
          host,
          storyDifficulities: storyDifficulities ? JSON.parse(storyDifficulities) : {},
        });
      }
    });
  }, []);

  return <RemoteConfigContext.Provider value={value}>{children}</RemoteConfigContext.Provider>;
};

/**
 * Provide custom hook for more convenient fetching of remote values.
 */
function useRemoteConfig() {
  const context = React.useContext(RemoteConfigContext);

  if (context === undefined) {
    throw new Error('Remote config must be used within RemoteConfigProvider');
  }

  return context;
}

export { RemoteConfigProvider, useRemoteConfig };
